<template>
  <div class="self-order-settings">
    <v-card>
      <v-card-title>
        <v-icon left>
          mdi-cellphone
        </v-icon>
        セルフオーダー設定
      </v-card-title>

      <v-card-text>
        <v-row>
          <!-- 左側: 設定パネル（QRコードタブ時は全幅） -->
          <v-col
            cols="12"
            :md="activeTab === 4 ? 12 : 6"
          >
            <v-tabs v-model="activeTab">
              <v-tab>テーマカラー</v-tab>
              <v-tab>画像設定</v-tab>
              <v-tab>カテゴリ設定</v-tab>
              <v-tab>商品画像</v-tab>
              <v-tab>QRコード</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- テーマカラー設定 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-row>
                      <v-col
                        v-for="colorField in colorFields"
                        :key="colorField.field"
                        cols="12"
                        sm="6"
                      >
                        <v-card outlined>
                          <v-card-subtitle>{{ colorField.label }}</v-card-subtitle>
                          <v-card-text>
                            <v-color-picker
                              :value="theme[colorField.field]"
                              mode="hexa"
                              hide-mode-switch
                              show-swatches
                              :swatches="swatches"
                              @input="updateColor(colorField.field, $event)"
                            />
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- 画像設定 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <image-uploader
                      v-model="logoFile"
                      label="店舗ロゴ"
                      hint="推奨: 200×200px（正方形）、上限1MB"
                      :preview-url="logoUrl"
                      @remove="removeImage('logo')"
                    />

                    <v-divider class="my-4" />

                    <image-uploader
                      v-model="bannerFile"
                      label="バナー画像"
                      hint="推奨: 800×400px（横長 2:1）、上限1MB"
                      :preview-url="bannerUrl"
                      @remove="removeImage('banner')"
                    />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- カテゴリ設定 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <category-settings
                      :categories="sortedCategories"
                      :available-tags="unselectedTags"
                      :icon-options="iconOptions"
                      @add="addCategory"
                      @remove="removeCategory"
                      @update-icon="updateCategoryIcon"
                      @reorder="reorderCategories"
                    />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- 商品画像設定 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <product-image-settings :categories="sortedCategories" />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- QRコード一覧 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <qr-code-list />
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>

            <v-card-actions v-if="activeTab < 3">
              <v-spacer />
              <v-btn
                color="primary"
                :loading="saving"
                :disabled="loading"
                @click="saveSettings"
              >
                <v-icon left>
                  {{ icons.mdiContentSave }}
                </v-icon>
                保存
              </v-btn>
            </v-card-actions>
          </v-col>

          <!-- 右側: プレビュー（QRコードタブ時は非表示） -->
          <v-col
            v-if="activeTab !== 4"
            cols="12"
            md="6"
          >
            <div class="preview-section">
              <div class="d-flex align-center mb-4">
                <span class="text-subtitle-1 font-weight-medium">プレビュー</span>
                <v-spacer />
                <v-btn-toggle
                  v-model="deviceSize"
                  mandatory
                  dense
                >
                  <v-btn
                    value="mobile"
                    small
                  >
                    モバイル
                  </v-btn>
                  <v-btn
                    value="tablet"
                    small
                  >
                    タブレット
                  </v-btn>
                </v-btn-toggle>
              </div>

              <mobile-order-preview
                :device-size="deviceSize"
                :theme="theme"
                :logo-url="logoPreviewUrl"
                :banner-url="bannerPreviewUrl"
                :categories="sortedCategories"
                :icon-options="iconOptions"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 成功/エラーメッセージ -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      timeout="3000"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  ref, computed, onMounted, watch,
} from '@vue/composition-api'
import { mdiContentSave } from '@mdi/js'
import { useStore } from '@/views/composable/useStore'
import ImageUploader from '@/components/admin/ImageUploader.vue'
import CategorySettings from '@/components/admin/CategorySettings.vue'
import MobileOrderPreview from '@/components/admin/MobileOrderPreview.vue'
import ProductImageSettings from '@/components/admin/ProductImageSettings.vue'
import QrCodeList from '@/components/admin/QrCodeList.vue'

export default {
  name: 'SelfOrderSettings',

  components: {
    ImageUploader,
    CategorySettings,
    MobileOrderPreview,
    ProductImageSettings,
    QrCodeList,
  },

  setup() {
    const store = useStore()
    const activeTab = ref(0)
    const deviceSize = ref('mobile')

    // 画像ファイル
    const logoFile = ref(null)
    const bannerFile = ref(null)

    // スナックバー
    const showSnackbar = ref(false)
    const snackbarMessage = ref('')
    const snackbarColor = ref('success')

    // カラーフィールド定義
    const colorFields = [
      { field: 'primaryColor', label: 'プライマリカラー（アクセント）' },
      { field: 'backgroundColor', label: '背景カラー' },
      { field: 'cardColor', label: 'カードカラー' },
      { field: 'textColor', label: 'テキストカラー' },
    ]

    // カラースウォッチ
    const swatches = [
      ['#d4bc83', '#c9a961', '#b8993f'],
      ['#1a1826', '#232030', '#2d2a3e'],
      ['#ffffff', '#f5f5f5', '#e0e0e0'],
      ['#f44336', '#e91e63', '#9c27b0'],
    ]

    // Vuexゲッター
    const loading = computed(() => store.getters['admin/selfOrderSettings/loading'])
    const saving = computed(() => store.getters['admin/selfOrderSettings/saving'])
    const theme = computed(() => store.getters['admin/selfOrderSettings/theme'])
    const logoUrl = computed(() => store.getters['admin/selfOrderSettings/logoUrl'])
    const bannerUrl = computed(() => store.getters['admin/selfOrderSettings/bannerUrl'])
    const sortedCategories = computed(() => store.getters['admin/selfOrderSettings/sortedCategories'])
    const unselectedTags = computed(() => store.getters['admin/selfOrderSettings/unselectedTags'])
    const iconOptions = computed(() => store.getters['admin/selfOrderSettings/iconOptions'])

    // プレビュー用URL（ファイル選択時はローカルURL）
    const logoPreviewUrl = computed(() => {
      if (logoFile.value) {
        return URL.createObjectURL(logoFile.value)
      }

      return logoUrl.value
    })

    const bannerPreviewUrl = computed(() => {
      if (bannerFile.value) {
        return URL.createObjectURL(bannerFile.value)
      }

      return bannerUrl.value
    })

    // メソッド
    const updateColor = (field, value) => {
      // カラーピッカーがオブジェクトを返す場合があるので対応
      const colorValue = typeof value === 'string' ? value : value.hex
      store.dispatch('admin/selfOrderSettings/updateThemeColor', { field, value: colorValue })
    }

    const addCategory = vendibleTagId => {
      store.dispatch('admin/selfOrderSettings/addCategory', vendibleTagId)
    }

    const removeCategory = vendibleTagId => {
      store.dispatch('admin/selfOrderSettings/removeCategory', vendibleTagId)
    }

    const updateCategoryIcon = ({ vendibleTagId, icon }) => {
      store.dispatch('admin/selfOrderSettings/updateCategoryIcon', { vendibleTagId, icon })
    }

    const reorderCategories = categories => {
      store.dispatch('admin/selfOrderSettings/reorderCategories', categories)
    }

    const removeImage = type => {
      // 画像削除処理（未保存の場合はファイルをクリア）
      switch (type) {
        case 'logo':
          logoFile.value = null
          break
        case 'banner':
          bannerFile.value = null
          break
        default:
          break
      }
    }

    const saveSettings = async () => {
      try {
        // テーマ設定を保存（カテゴリは各操作時に即時保存済み）
        await store.dispatch('admin/selfOrderSettings/saveSetting', {
          logo: logoFile.value,
          banner: bannerFile.value,
        })

        // ファイル入力をクリア
        logoFile.value = null
        bannerFile.value = null

        snackbarMessage.value = '設定を保存しました'
        snackbarColor.value = 'success'
        showSnackbar.value = true
      } catch (error) {
        snackbarMessage.value = '保存に失敗しました'
        snackbarColor.value = 'error'
        showSnackbar.value = true
      }
    }

    // 初期化
    onMounted(async () => {
      try {
        await Promise.all([
          store.dispatch('admin/selfOrderSettings/fetchSetting'),
          store.dispatch('admin/selfOrderSettings/fetchVendibleTags'),
        ])
      } catch (error) {
        snackbarMessage.value = '設定の読み込みに失敗しました'
        snackbarColor.value = 'error'
        showSnackbar.value = true
      }
    })

    return {
      // data
      activeTab,
      deviceSize,
      logoFile,
      bannerFile,
      showSnackbar,
      snackbarMessage,
      snackbarColor,
      colorFields,
      swatches,

      // computed
      loading,
      saving,
      theme,
      logoUrl,
      bannerUrl,
      logoPreviewUrl,
      bannerPreviewUrl,
      sortedCategories,
      unselectedTags,
      iconOptions,

      // methods
      updateColor,
      addCategory,
      removeCategory,
      updateCategoryIcon,
      reorderCategories,
      removeImage,
      saveSettings,

      // icons
      icons: {
        mdiContentSave,
      },
    }
  },
}
</script>

<style scoped>
.self-order-settings {
  padding: 16px;
}

.preview-section {
  position: sticky;
  top: 80px;
}
</style>
